import { ReactComponent as StatusIcon } from '@assets/components/panel/monitoring/status.svg';
import { IcWorks16 } from '@assets/icons/16x16';
import { IcAltitude } from '@assets/icons/24x24';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoWithoutSection } from '@components/cockpit/panels/monitoring/monitoring.types';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectAltitudeUnit } from '@redux/settings/settings.selectors';
import { getSensorOrSubSensorAltitude } from '@utils/map/sensor.utils';
import { getSensorHexColor, getSensorStatusLabelKey, hasSpecificState } from '@utils/sensors/sensors.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import IconValue from '../../IconValue';
import InfoValue from '../../InfoValue';
import SensorCoordinatesInformation from './SensorCoordinatesInformation';

type InformationProps = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoWithoutSection;
};

export default function Information({ sensorOrSubSensorInfo }: Readonly<InformationProps>) {
  const { formatMessage } = useIntl();
  const altitudeUnit = useAppSelector(selectAltitudeUnit);

  const altitude = getSensorOrSubSensorAltitude(sensorOrSubSensorInfo.configuration);
  const status = sensorOrSubSensorInfo.status;
  const specificState = status && hasSpecificState(status) ? status.specificState : undefined;
  return (
    <VStack gap={0.25} width="100%">
      <PopupContainer>
        <HStack width="100%" gap="standardXS">
          <InfoValue
            label={formatMessage({ id: 'sensors.tab.information.status' })}
            value={
              <IconValue
                customIcon={
                  <Icon
                    as={sensorOrSubSensorInfo.maintenance ? IcWorks16 : StatusIcon}
                    boxSize={{ base: '16px', '4k': '24px' }}
                    color={getSensorHexColor(sensorOrSubSensorInfo)}
                  />
                }
                value={formatMessage({
                  id: getSensorStatusLabelKey(sensorOrSubSensorInfo),
                })}
              />
            }
          />
          {specificState !== undefined && (
            <InfoValue
              label={formatMessage({ id: 'sensors.tab.information.state' })}
              value={formatMessage({
                id: specificState ? `sensors.state.${specificState}` : 'global.NA',
              })}
            />
          )}
        </HStack>
      </PopupContainer>
      <PopupContainer>
        <VStack gap="standardXS" textStyle="screenStd" width="100%" alignItems="start">
          <Text color="neutral.300">
            <FormattedMessage id="sensors.tab.information.fixedPosition" />
          </Text>
          <HStack width="100%" gap={0}>
            <IconValue
              icon={IcAltitude}
              value={formatUnit(altitudeUnit, convertDistanceUnitFromMeter(altitudeUnit, altitude))}
            />
          </HStack>
        </VStack>
      </PopupContainer>
      <SensorCoordinatesInformation sensorOrSubSensorInfo={sensorOrSubSensorInfo} />
    </VStack>
  );
}
